import cx from "classnames";
import { Loadable } from "common/Loadable";
import { MapAndGraph } from "common/MapAndGraph";
import { ShareDialog, SHARE_DIALOG } from "common/ShareDialog";
import { CircleSpinner } from "common/spinners";
import { CTA } from "common/styleguide/CTA";
import { Distance, Duration, Elevation, Grade, Speed } from "common/units";
import { gearSelectors } from "data/resources/gears";
import { tripActions, tripSelectors } from "data/resources/trips";
import { commonMetrics, commonNouns, commonVerbs, featureNames, fm, Msg, timeLabels } from "lib/localization";
import { j } from "lib/textUtils";
import { urls } from "lib/urls";
import _ from "lodash";
import moment from "moment";
import React, { Fragment } from "react";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { troutesMessages } from "../../shared/intlMessages";
import styles from "./ExpandedRidePreview.scss";

export class ExpandedRidePreview extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      dialog: null,
      tripName: null,
      editable: false,
      updatePending: false,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.loadableResources && !prevState.tripName) {
      this.setState({ tripName: this.props.trip.name });
    }
  }

  hideDialog = () => this.setState({ dialog: null });

  showDialog = () => this.setState({ dialog: SHARE_DIALOG });

  toggleEditing = () => {
    this.setState({ editing: !this.state.editing });
  };

  onRideNameChange = (e) => {
    this.setState({ tripName: e.target.value });
  };

  saveTripName = () => {
    const { tripName, updatePending } = this.state;
    if (updatePending) {
      return null;
    }
    const { trip, updateTrip } = this.props;

    this.setState({ updatePending: true }, () => {
      updateTrip(trip.id, { trip: { name: tripName } }).then((r) =>
        this.setState({ editing: false, updatePending: false })
      );
    });
  };

  render() {
    const { dialog, editing, updatePending, tripName } = this.state;
    const { trip, gear, metricUnits, intl } = this.props;
    let { metrics } = trip;
    if (!metrics) metrics = {};

    return (
      <div className={styles.ExpandedRidePreview}>
        <div className={styles.left}>
          {editing ? (
            <div className={styles.editingTripName}>
              <input value={tripName || ""} onChange={this.onRideNameChange} className={styles.tripNameInput} />
              <span className={styles.saveButton}>
                <CTA secondary margins={false} disabled={updatePending} onClick={this.saveTripName}>
                  {updatePending ? <CircleSpinner size={14} /> : fm(intl, commonVerbs.save)}
                </CTA>
              </span>
            </div>
          ) : (
            <h3>
              <span className={styles.tripName} onClick={this.toggleEditing}>
                {trip.name}
              </span>
            </h3>
          )}
          <div className={styles.metrics}>
            <div className={styles.metricsSection}>
              <MetricsRow
                label={fm(intl, troutesMessages.departed)}
                value={moment(trip.departedAt).format("MMM DD, 'YY, HH:mmA")}
              />
              <MetricsRow
                label={fm(intl, troutesMessages.startsIn)}
                value={j([trip.locality, trip.administrativeArea]) || "-"}
              />
              <MetricsRow label={fm(intl, commonMetrics.distance)} M={Distance} short value={trip.distance} />
              <div className={styles.metricsRow}>
                <div className={styles.metricsCol}>
                  <Msg m={commonMetrics.elevation} />:
                </div>
                <div className={styles.metricsCol}>
                  +<Elevation short value={trip.elevationGain} />
                  /-
                  <Elevation short value={trip.elevationLoss} />
                </div>
              </div>
              {metrics.grade && (
                <MetricsRow label={fm(intl, commonMetrics.maxGrade)} M={Grade} short value={metrics.grade.max} />
              )}
              {gear && (
                <MetricsRow
                  label={fm(intl, featureNames.gear)}
                  value={<a href={urls.gear.show(gear.id)}>{gear.name}</a>}
                />
              )}
            </div>

            {(metrics.duration || metrics.movingTime || metrics.stoppedTime) && (
              <div className={styles.metricsSection}>
                {metrics.duration && (
                  <MetricsRow
                    label={fm(intl, commonMetrics.totalDuration)}
                    title={`${fm(intl, commonMetrics.totalDuration)} Total Duration (${fm(intl, timeLabels.hhmm)})`}
                    M={Duration}
                    value={metrics.duration}
                    bigUnits
                    valueOnly
                  />
                )}
                {metrics.movingTime && (
                  <MetricsRow
                    label={fm(intl, commonMetrics.movingTime)}
                    title={`${fm(intl, commonMetrics.movingTime)} (${fm(intl, timeLabels.hhmm)})`}
                    M={Duration}
                    value={metrics.movingTime}
                    bigUnits
                    valueOnly
                  />
                )}
                {typeof metrics.stoppedTime === "number" && (
                  <MetricsRow
                    label={fm(intl, commonMetrics.stoppedTime)}
                    title={`${fm(intl, commonMetrics.stoppedTime)} (${fm(intl, timeLabels.hhmm)})`}
                    M={Duration}
                    value={metrics.stoppedTime}
                    bigUnits
                    valueOnly
                  />
                )}
              </div>
            )}

            {(trip.calories || trip.avgWatts) && (
              <div className={styles.metricsSection}>
                {trip.calories && (
                  <MetricsRow label={`${_.capitalize(fm(intl, commonMetrics.calories))}`} value={trip.calories} />
                )}{" "}
                {/* this one needs to come from trip for some reason */}
                {trip.avgWatts && (
                  <MetricsRow label={`${fm(intl, commonMetrics.avgWatts)}`} value={Math.round(trip.avgWatts)} />
                )}
              </div>
            )}

            <div className={cx(styles.metricsSection, styles.noBorder)}>
              {metrics.speed && (
                <MetricsRow label={`${fm(intl, commonMetrics.maxSpeed)}`} M={Speed} short value={metrics.speed.max} />
              )}
              {metrics.speed && (
                <MetricsRow label={`${fm(intl, commonMetrics.avgSpeed)}`} M={Speed} short value={metrics.speed.avg} />
              )}
            </div>

            {trip.description && <div className={styles.description}>{trip.description}</div>}
          </div>

          <div className={styles.links}>
            <div className={styles.linkWrap}>
              <a href={urls.trips.show(trip.id, trip.privacyCode)}>
                <Msg m={troutesMessages.viewFullDetails} />
              </a>
            </div>
            <div className={styles.linkWrap}>
              <a onClick={this.showDialog}>
                <Msg m={commonVerbs.share} />
              </a>
              {dialog === SHARE_DIALOG && (
                <ShareDialog
                  onRequestClose={this.hideDialog}
                  objectType="trip"
                  objectId={trip.id}
                  objectTitle={trip.name}
                  privacyCode={trip.privacyCode}
                  objectUrl={urls.trips.show(trip.id, trip.privacyCode)}
                />
              )}
            </div>
          </div>
        </div>

        <div className={styles.right}>
          {trip.isStationary || trip.trackPoints.length === 0 ? (
            <img src={urls.staticMap("trip", "hover_preview@2x", trip.id)} style={{ width: "100%", height: "100%" }} />
          ) : (
            <MapAndGraph height="500" troute={trip} metricUnits={metricUnits} />
          )}
        </div>
      </div>
    );
  }
}

const MetricsRow = ({ M, label, value, title, ...metricProps }) => {
  if (M !== undefined) {
    return (
      value !== null && (
        <div className={styles.metricsRow}>
          <div className={styles.metricsCol}>{label}:</div>
          <div className={styles.metricsCol}>
            <span data-tip={!!title} title={title}>
              <M value={value} {...metricProps} />
            </span>
          </div>
        </div>
      )
    );
  } else {
    return (
      value && (
        <div className={styles.metricsRow}>
          <div className={styles.metricsCol}>{label}:</div>
          <div className={styles.metricsCol}>
            <Fragment>{value}</Fragment>
          </div>
        </div>
      )
    );
  }
};

const loadInitialResources = _.memoize(({ troute, loadTrip }) => loadTrip(troute.id));

const _LoadableExpandedRidePreview = (props) => (
  <Loadable
    {...props}
    component={ExpandedRidePreview}
    loadingText={fm(props.intl, troutesMessages.loading, {
      type: _.capitalize(fm(props.intl, commonNouns.trips, { count: 1 })),
    })}
    resourceLoaded={() => loadInitialResources(props)}
  />
);

const mapStateToProps = (state, ownProps) => ({
  trip: tripSelectors.find(state, ownProps.troute.id),
  gear: gearSelectors.find(state, ownProps.troute.gearId),
});

const mapDispatchToProps = {
  loadTrip: tripActions.read,
  updateTrip: tripActions.update,
};

export const LoadableExpandedRidePreview = connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(_LoadableExpandedRidePreview));
